<script setup lang="ts">
import { computed } from 'vue';
import { Guest } from '../../types/guest';
import { TableShape } from '../../types/table';

interface Props {
  guests: Guest[];
  tableShape: TableShape;
  tableNumber: number;
  selectedGuestId: string | null;
  isSelected: boolean;
  onGuestClick: (guest: Guest) => void;
  onAddGuest: (seatIndex: number) => void;
  seatCount: number;
  length?: number | undefined;
  width?: number | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  length: 4,
  width: 2,
});

const emit = defineEmits<{
  (e: 'guestClick', guest: Guest): void;
  (e: 'addGuest', seatIndex: number): void;
  (e: 'showUnassignedGuests', seatIndex: number): void;
}>();

const actualSeatCount = computed(() => {
  if (props.tableShape === TableShape.RECTANGULAR) {
    const length = props.length;
    const width = props.width;
    return 2 * (length + width) - 4; // Subtract 4 corners that are counted twice
  }
  return props.seatCount;
});

const tableSize = computed(() => {
  const baseSize = 120;
  const minSize = 100;
  const maxSize = 3000;
  const seatCount = props.seatCount;

  if (props.tableShape === TableShape.RECTANGULAR) {
    const length = props.length;
    const width = props.width;

    const baseDimension = baseSize + (seatCount - 4) * 3;

    const aspectRatio = length / width;

    let finalWidth = 0,
      finalHeight = 0;

    if (aspectRatio >= 1) {
      finalWidth = baseDimension * Math.min(aspectRatio, 3);
      finalHeight = baseDimension;
    } else {
      finalWidth = baseDimension;
      finalHeight = baseDimension / aspectRatio;
    }

    finalWidth = Math.max(finalWidth, minSize);
    finalHeight = Math.max(finalHeight, minSize);

    return {
      width: `${Math.min(finalWidth, maxSize)}px`,
      height: `${Math.min(finalHeight, maxSize)}px`,
    };
  }

  const size = baseSize + (seatCount - 4) * 15;
  return {
    width: `${Math.min(Math.max(size, minSize), maxSize)}px`,
    height: `${Math.min(Math.max(size, minSize), maxSize)}px`,
  };
});

const seatSize = computed(() => {
  const baseSize = 30;
  const minSize = 25;
  const maxSize = 35;
  const seatCount = props.seatCount;

  if (props.tableShape === TableShape.RECTANGULAR) {
    const length = props.length;
    const width = props.width;

    const seatsOnPerimeter = 2 * (length + width) - 4;

    const tableWidth = parseInt(tableSize.value.width);
    const tableHeight = parseInt(tableSize.value.height);

    const padding = 25;
    const availableWidth = tableWidth - 2 * padding;
    const availableHeight = tableHeight - 2 * padding;
    const totalPerimeter = 2 * (availableWidth + availableHeight);

    const availableSpace = totalPerimeter / seatsOnPerimeter;
    return Math.min(Math.max(availableSpace * 0.4, minSize), maxSize);
  }

  const size = baseSize + (seatCount - 4) * 0.5;
  return Math.min(Math.max(size, minSize), maxSize);
});

const tableStyle = computed(() => ({
  width: tableSize.value.width,
  height: tableSize.value.height,
  borderRadius: props.tableShape === TableShape.ROUND ? '50%' : '8px',
  backgroundColor: '#f2e9e4',
  border: '2px solid #834920',
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
}));

const getSeatPosition = (index: number, total: number) => {
  if (props.tableShape === TableShape.RECTANGULAR) {
    const length = props.length;
    const width = props.width;

    const tableWidth = parseInt(tableSize.value.width);
    const tableHeight = parseInt(tableSize.value.height);

    const padding = 25;
    const availableWidth = tableWidth - 2 * padding;
    const availableHeight = tableHeight - 2 * padding;

    // Calculate seats per side
    const seatsOnTop = length;
    const seatsOnRight = width;
    const seatsOnBottom = length;
    const seatsOnLeft = width;

    let x = 0,
      y = 0;

    if (index < seatsOnTop) {
      // Top edge
      x = padding + (index * availableWidth) / (seatsOnTop - 1);
      y = padding;
    } else if (index < seatsOnTop + seatsOnRight - 1) {
      // Right edge
      x = tableWidth - padding;
      y = padding + ((index - seatsOnTop + 1) * availableHeight) / (seatsOnRight - 1);
    } else if (index < seatsOnTop + seatsOnRight + seatsOnBottom - 2) {
      // Bottom edge
      const bottomIndex = index - (seatsOnTop + seatsOnRight - 2);
      x = tableWidth - padding - (bottomIndex * availableWidth) / (seatsOnBottom - 1);
      y = tableHeight - padding;
    } else {
      // Left edge
      const leftIndex = index - (seatsOnTop + seatsOnRight + seatsOnBottom - 3);
      x = padding;
      y = tableHeight - padding - (leftIndex * availableHeight) / (seatsOnLeft - 1);
    }

    return {
      left: `${x - seatSize.value / 2}px`,
      top: `${y - seatSize.value / 2}px`,
    };
  }

  const angle = (index * 2 * Math.PI) / total;
  const radius = parseInt(tableSize.value.width) / 2 - seatSize.value;
  return {
    left: `${parseInt(tableSize.value.width) / 2 + radius * Math.cos(angle) - seatSize.value / 2}px`,
    top: `${parseInt(tableSize.value.height) / 2 + radius * Math.sin(angle) - seatSize.value / 2}px`,
  };
};

const isSeatOccupied = (index: number) => {
  return props.guests.some((guest) => guest.seatIndex === index);
};

const getGuestAtSeat = (index: number) => {
  return props.guests.find((guest) => guest.seatIndex === index);
};

const handleSeatClick = (seatIndex: number) => {
  const guest = getGuestAtSeat(seatIndex);
  if (props.selectedGuestId) {
    emit('addGuest', seatIndex);
  } else if (guest) {
    emit('guestClick', guest);
  } else {
    emit('showUnassignedGuests', seatIndex);
  }
};
</script>

<template>
  <div class="table-visualization">
    <div :style="tableStyle">
      <div class="table-number">{{ tableNumber }}</div>
      <div
        v-for="index in actualSeatCount"
        v-show="isSeatOccupied(index - 1) || isSelected || selectedGuestId"
        :key="index"
        class="seat"
        :style="getSeatPosition(index - 1, actualSeatCount)"
        :class="{
          'seat--occupied': isSeatOccupied(index - 1),
          'seat--selected': getGuestAtSeat(index - 1)?.id === selectedGuestId,
          'seat--available': !isSeatOccupied(index - 1),
        }"
        @click.stop="handleSeatClick(index - 1)"
      >
        <div
          v-if="isSeatOccupied(index - 1)"
          class="seat__content"
        >
          {{
            getGuestAtSeat(index - 1)
              ?.name.charAt(0)
              .toUpperCase()
          }}{{
            getGuestAtSeat(index - 1)
              ?.surname.charAt(0)
              .toUpperCase()
          }}
          <div class="seat__tooltip">
            {{ getGuestAtSeat(index - 1)?.name }} {{ getGuestAtSeat(index - 1)?.surname }}
          </div>
        </div>
        <div
          v-else-if="isSelected || selectedGuestId"
          class="seat__content seat__content--add"
        >
          <i class="pi pi-plus"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.table-visualization {
  position: relative;
}

.table-number {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  font-weight: bold;
  color: #834920;
  z-index: 1;
}

.seat {
  position: absolute;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid #834920;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1;

  &__tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #834920;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-size: 0.875rem;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    z-index: 4;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-width: 5px;
      border-style: solid;
      border-color: #834920 transparent transparent;
    }
  }

  &:hover {
    transform: scale(1.1);
    background-color: #f2e9e4;
    z-index: 3;

    .seat__tooltip {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, -6px);
    }
  }

  &--occupied {
    background-color: #f2e9e4;
    cursor: pointer;
  }

  &--selected {
    background-color: #f2e9e4;
    border-color: $status-green;
  }

  &--available {
    background-color: $status-green;
    border-color: $status-green;
    color: white;
  }

  &__content {
    font-size: 0.8rem;
    font-weight: bold;
    color: #834920;
    text-align: center;
    text-transform: uppercase;
    position: relative;

    &--add {
      color: white;
    }

    &--empty {
      color: #834920;
    }
  }
}
</style>
